import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Code, Upload, Download, Copy, Check, AlertCircle, Settings } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface FormatOptions {
  indentSize: number;
  indentType: 'spaces' | 'tabs';
  removeComments: boolean;
  removeEmptyLines: boolean;
  sortAttributes: boolean;
}

export const XmlFormatterTool: React.FC = () => {
  const { t } = useTranslation();
  const [xmlInput, setXmlInput] = useState('');
  const [xmlOutput, setXmlOutput] = useState('');
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [validationError, setValidationError] = useState('');
  const [options, setOptions] = useState<FormatOptions>({
    indentSize: 2,
    indentType: 'spaces',
    removeComments: false,
    removeEmptyLines: true,
    sortAttributes: false
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateXML = (xmlString: string): { isValid: boolean; error?: string } => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
      
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        return {
          isValid: false,
          error: parseError.textContent || 'XML parsing error'
        };
      }
      
      return { isValid: true };
    } catch (error) {
      return {
        isValid: false,
        error: error instanceof Error ? error.message : 'Unknown XML error'
      };
    }
  };

  const formatXML = (xmlString: string, options: FormatOptions): string => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
      
      // Check for parsing errors
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        throw new Error('Invalid XML');
      }

      const indent = options.indentType === 'tabs' ? '\t' : ' '.repeat(options.indentSize);
      
      const formatNode = (node: Node, level: number = 0): string => {
        const currentIndent = indent.repeat(level);
        const nextIndent = indent.repeat(level + 1);
        
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          return text ? text : '';
        }
        
        if (node.nodeType === Node.COMMENT_NODE) {
          if (options.removeComments) {
            return '';
          }
          return `${currentIndent}<!--${node.textContent}-->`;
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          let result = `${currentIndent}<${element.tagName}`;
          
          // Handle attributes
          const attributes = Array.from(element.attributes);
          if (options.sortAttributes) {
            attributes.sort((a, b) => a.name.localeCompare(b.name));
          }
          
          for (const attr of attributes) {
            result += ` ${attr.name}="${attr.value}"`;
          }
          
          const children = Array.from(node.childNodes);
          const hasElementChildren = children.some(child => child.nodeType === Node.ELEMENT_NODE);
          const hasTextContent = children.some(child => 
            child.nodeType === Node.TEXT_NODE && child.textContent?.trim()
          );
          
          if (children.length === 0) {
            result += '/>';
            return result;
          }
          
          result += '>';
          
          if (hasElementChildren || (!hasTextContent && children.length > 1)) {
            // Multi-line format
            const childResults = children
              .map(child => formatNode(child, level + 1))
              .filter(childResult => childResult.trim() !== '');
            
            if (childResults.length > 0) {
              result += '\n' + childResults.join('\n') + '\n' + currentIndent;
            }
          } else {
            // Inline format for simple text content
            const textContent = children
              .filter(child => child.nodeType === Node.TEXT_NODE)
              .map(child => child.textContent?.trim())
              .join('');
            result += textContent;
          }
          
          result += `</${element.tagName}>`;
          return result;
        }
        
        return '';
      };

      let formatted = '';
      
      // Handle XML declaration
      if (xmlString.includes('<?xml')) {
        const declarationMatch = xmlString.match(/<\?xml[^>]*\?>/);
        if (declarationMatch) {
          formatted += declarationMatch[0] + '\n';
        }
      }
      
      // Format document element
      const documentElement = xmlDoc.documentElement;
      if (documentElement) {
        formatted += formatNode(documentElement);
      }
      
      // Remove empty lines if option is set
      if (options.removeEmptyLines) {
        formatted = formatted.replace(/^\s*\n/gm, '');
      }
      
      return formatted;
    } catch (error) {
      throw new Error('Failed to format XML: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleFormat = () => {
    if (!xmlInput.trim()) {
      toast.error('Please enter XML content');
      return;
    }

    try {
      const validation = validateXML(xmlInput);
      setIsValid(validation.isValid);
      
      if (!validation.isValid) {
        setValidationError(validation.error || 'Invalid XML');
        setXmlOutput('');
        return;
      }
      
      setValidationError('');
      const formatted = formatXML(xmlInput, options);
      setXmlOutput(formatted);
      toast.success('XML formatted successfully');
    } catch (error) {
      setIsValid(false);
      setValidationError(error instanceof Error ? error.message : 'Formatting failed');
      setXmlOutput('');
      toast.error('Failed to format XML');
    }
  };

  const handleMinify = () => {
    if (!xmlInput.trim()) {
      toast.error('Please enter XML content');
      return;
    }

    try {
      const validation = validateXML(xmlInput);
      if (!validation.isValid) {
        setValidationError(validation.error || 'Invalid XML');
        return;
      }

      // Simple minification - remove unnecessary whitespace
      let minified = xmlInput
        .replace(/>\s+</g, '><')  // Remove whitespace between tags
        .replace(/\s+/g, ' ')     // Replace multiple spaces with single space
        .trim();
      
      setXmlOutput(minified);
      setIsValid(true);
      setValidationError('');
      toast.success('XML minified successfully');
    } catch (error) {
      toast.error('Failed to minify XML');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xml') && file.type !== 'text/xml' && file.type !== 'application/xml') {
        toast.error('Please select an XML file');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setXmlInput(content);
      };
      reader.readAsText(file);
    }
  };

  const downloadXML = () => {
    if (!xmlOutput) {
      toast.error('No formatted XML to download');
      return;
    }

    const blob = new Blob([xmlOutput], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const loadSampleXML = () => {
    const sampleXML = `<?xml version="1.0" encoding="UTF-8"?>
<bookstore>
<book id="1" category="fiction">
<title lang="en">Great Gatsby</title>
<author>F. Scott Fitzgerald</author>
<year>1925</year>
<price>10.99</price>
</book>
<book id="2" category="fiction">
<title lang="en">1984</title>
<author>George Orwell</author>
<year>1949</year>
<price>8.99</price>
</book>
</bookstore>`;
    setXmlInput(sampleXML);
  };

  const clearAll = () => {
    setXmlInput('');
    setXmlOutput('');
    setIsValid(null);
    setValidationError('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Code className="w-5 h-5" />
            <span>{t('tools.xmlFormatter.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('common.uploadFile')}
            </Button>
            <Button
              onClick={loadSampleXML}
              variant="outline"
            >
              {t('common.loadSample')}
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
            >
              {t('common.clearAll')}
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml,text/xml,application/xml"
            onChange={handleFileUpload}
            className="hidden"
          />
        </CardContent>
      </Card>

      {/* Format Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>Format Options</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Indent Type
                </label>
                <select
                  value={options.indentType}
                  onChange={(e) => setOptions({ ...options, indentType: e.target.value as 'spaces' | 'tabs' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="spaces">Spaces</option>
                  <option value="tabs">Tabs</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Indent Size
                </label>
                <input
                  type="number"
                  min="1"
                  max="8"
                  value={options.indentSize}
                  onChange={(e) => setOptions({ ...options, indentSize: parseInt(e.target.value) || 2 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={options.removeComments}
                  onChange={(e) => setOptions({ ...options, removeComments: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm">Remove comments</span>
              </label>
              
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={options.removeEmptyLines}
                  onChange={(e) => setOptions({ ...options, removeEmptyLines: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm">Remove empty lines</span>
              </label>
              
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={options.sortAttributes}
                  onChange={(e) => setOptions({ ...options, sortAttributes: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm">Sort attributes alphabetically</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Input/Output */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* XML Input */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>XML Input</span>
              <div className="flex space-x-2">
                <Button
                  onClick={handleFormat}
                  disabled={!xmlInput.trim()}
                  variant="primary"
                  size="sm"
                >
                  Format
                </Button>
                <Button
                  onClick={handleMinify}
                  disabled={!xmlInput.trim()}
                  variant="outline"
                  size="sm"
                >
                  Minify
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={xmlInput}
              onChange={(e) => setXmlInput(e.target.value)}
              placeholder="Paste your XML here or upload a file..."
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-mono text-sm"
            />
            
            {/* Validation Status */}
            {isValid !== null && (
              <div className={`mt-2 p-2 rounded-md text-sm ${
                isValid 
                  ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                  : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
              }`}>
                <div className="flex items-center space-x-2">
                  {isValid ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  <span>
                    {isValid ? 'Valid XML' : `Invalid XML: ${validationError}`}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* XML Output */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Formatted XML</span>
              {xmlOutput && (
                <div className="flex space-x-2">
                  <Button
                    onClick={() => copyToClipboard(xmlOutput)}
                    variant="outline"
                    size="sm"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                  <Button
                    onClick={downloadXML}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={xmlOutput}
              readOnly
              placeholder="Formatted XML will appear here..."
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white font-mono text-sm"
            />
          </CardContent>
        </Card>
      </div>

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Features:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• XML validation and error detection</li>
                <li>• Customizable indentation (spaces or tabs)</li>
                <li>• Comment removal option</li>
                <li>• Attribute sorting</li>
                <li>• XML minification</li>
                <li>• File upload and download support</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Tips:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Paste XML directly or upload .xml files</li>
                <li>• Use Format for readable, indented output</li>
                <li>• Use Minify to compress XML for production</li>
                <li>• Validation errors show specific issues</li>
                <li>• Download formatted XML as .xml file</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
